import { useEffect, useMemo, useState } from 'react'
import { getSendStatus, listSignatures, sendProspectEmail, type RemoteProspect, type SignatureView } from '../../lib/adminClient'
import { useAuth } from '../../lib/auth'
import { Linkify, StyleIssues, inputCls, selectCls, useStyleCheck } from './ui'

// Everyone ready for an email, one at a time: read it, change it if you like,
// send it, and the next one comes up. Each send is still one email to one
// person through the server's own checks, and the day's limit is shown so the
// domain's reputation is never spent in one sitting.

export function SendQueue({ people, onSent, onOpenPerson }: { people: RemoteProspect[]; onSent: (p: RemoteProspect) => void; onOpenPerson: (id: string) => void }): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role === 'owner' || session?.role === 'admin'
  const [status, setStatus] = useState<{ sentToday: number; cap: number } | null>(null)
  const [sigs, setSigs] = useState<SignatureView[]>([])
  const [skipped, setSkipped] = useState<string[]>([])
  const [sentNow, setSentNow] = useState<string[]>([])
  useEffect(() => {
    void getSendStatus().then((r) => r.ok && setStatus({ sentToday: r.data.sentToday, cap: r.data.cap }))
    void listSignatures().then((r) => r.ok && setSigs(r.data.signatures))
  }, [])

  const queue = useMemo(
    () =>
      people
        .filter((p) => p.plan.step === 'email' && p.email && p.emailSource && !skipped.includes(p.id) && !sentNow.includes(p.id))
        .sort((a, b) => (b.score ?? -1) - (a.score ?? -1)),
    [people, skipped, sentNow]
  )
  const current = queue[0] ?? null
  const left = status ? Math.max(0, status.cap - status.sentToday) : null

  return (
    <div className="space-y-4" data-testid="send-queue">
      <div className="glass-card rounded-xl px-4 py-3 text-sm flex flex-wrap items-center gap-3">
        <span>
          <span className="font-medium text-white" data-testid="queue-count">{queue.length}</span> ready to email
        </span>
        {status && (
          <span className="text-slate-400" data-testid="queue-cap">
            · {status.sentToday} of {status.cap} sent in the last 24 hours (the daily limit keeps connect@ out of spam folders)
          </span>
        )}
        {sentNow.length > 0 && <span className="text-emerald-300">· {sentNow.length} sent this session</span>}
        {sigs.length === 0 && <span className="text-amber-200" data-testid="queue-no-signature">· No signature yet: add one in Signatures &amp; guide, or emails go out signed only with the company footer.</span>}
      </div>
      {!current ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="queue-empty">
          Nobody is waiting for an email. Find more published addresses on the People tab, or work through the LinkedIn and contact-form steps there.
        </div>
      ) : (
        <QueueItem
          key={current.id}
          p={current}
          sigs={sigs}
          canWrite={canWrite}
          limitReached={left === 0}
          onSkip={() => setSkipped([...skipped, current.id])}
          onOpen={() => onOpenPerson(current.id)}
          onSent={(updated) => {
            setSentNow([...sentNow, current.id])
            setStatus((s) => (s ? { ...s, sentToday: s.sentToday + 1 } : s))
            onSent(updated)
          }}
        />
      )}
      {queue.length > 1 && (
        <div className="text-xs text-slate-500" data-testid="queue-next">
          Next: {queue.slice(1, 6).map((p) => p.name).join(', ')}
          {queue.length > 6 ? ` and ${queue.length - 6} more` : ''}
        </div>
      )}
    </div>
  )
}

function QueueItem({
  p,
  sigs,
  canWrite,
  limitReached,
  onSkip,
  onOpen,
  onSent
}: {
  p: RemoteProspect
  sigs: SignatureView[]
  canWrite: boolean
  limitReached: boolean
  onSkip: () => void
  onOpen: () => void
  onSent: (p: RemoteProspect) => void
}): JSX.Element {
  const [subject, setSubject] = useState(p.draftEmailSubject ?? '')
  const [body, setBody] = useState(p.draftEmail ?? `Hi ${p.name.replace(/\([^)]*\)/g, '').trim().split(/\s+/)[0]},\n\n`)
  const [sig, setSig] = useState(sigs.find((s) => s.isDefault)?.id ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const issues = useStyleCheck(body, 'email', p.name)
  useEffect(() => setSig(sigs.find((s) => s.isDefault)?.id ?? ''), [sigs])
  const campaignId = p.campaigns.find((c) => c.state === 'subscribed')?.campaignId ?? null

  async function send(): Promise<void> {
    setBusy(true)
    setErr(null)
    const r = await sendProspectEmail(p.id, { subject, body, signatureId: sig || null, campaignId })
    setBusy(false)
    if (!r.ok) return setErr(r.error)
    onSent(r.data.prospect)
  }

  return (
    <div className="glass-card rounded-xl p-5 space-y-3" data-testid={`queue-item-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{p.name}</div>
          <div className="text-sm text-slate-300">{[p.role, p.company].filter(Boolean).join(' · ')}</div>
          <div className="text-xs text-slate-400 mt-1">
            To <span className="text-slate-200">{p.email}</span> · <Linkify text={p.emailSource} />
          </div>
        </div>
        <button className="text-xs text-accent hover:underline" onClick={onOpen}>
          Open their page (facts, history)
        </button>
      </div>
      {p.watchOuts && <div className="text-xs text-amber-200 bg-amber-500/5 rounded-md px-3 py-2">Careful: <Linkify text={p.watchOuts} /></div>}
      {/united kingdom|ireland/i.test(p.country ?? '') && (
        <div className="text-xs text-amber-200" data-testid="queue-caution">UK/Irish rules (PECR): fine for a company’s published business address; a sole trader’s or personal address needs consent first.</div>
      )}
      <input className={inputCls} value={subject} readOnly={!canWrite} onChange={(e) => setSubject(e.target.value)} data-testid="queue-subject" />
      <textarea className={inputCls} rows={8} value={body} readOnly={!canWrite} onChange={(e) => setBody(e.target.value)} data-testid="queue-body" />
      <StyleIssues issues={issues} />
      <div className="flex flex-wrap items-center gap-2">
        <select className={selectCls} value={sig} onChange={(e) => setSig(e.target.value)} data-testid="queue-signature">
          <option value="">No signature</option>
          {sigs.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {canWrite && (
          <button className="btn-primary" disabled={busy || limitReached || !subject.trim() || !body.trim()} onClick={() => void send()} data-testid="queue-send">
            {busy ? 'Sending…' : `Send to ${p.email}`}
          </button>
        )}
        <button className="btn-ghost" onClick={onSkip} data-testid="queue-skip">
          Skip for now
        </button>
        {limitReached && <span className="text-xs text-amber-200">Today’s limit is reached. The next can go tomorrow.</span>}
      </div>
      {err && <div className="text-xs text-red-300" data-testid="queue-error">{err}</div>}
    </div>
  )
}
